import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { candidateAiRecruiterOptOut } from "model/CandidateModel";

const optOutSchema = Yup.object().shape({
  notes: Yup.string().trim().required("Please enter notes."),
});

/**
 * Opt out of AI Recruiter — notes required.
 * POST `Candidate/{candidateClientId}/ai-recruiter-opt-out`
 */
const CallDetailsAiRecruiterOptOutModal = ({
  isOpen,
  onClose,
  candidateClientId,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setNotes("");
    setErrors({});
  }, [isOpen]);

  const closeModal = () => {
    if (!submitting) onClose?.();
  };

  const handleNotesChange = async (event) => {
    const nextNotes = event.target.value;
    setNotes(nextNotes);
    if (!errors.notes) return;
    try {
      await Yup.reach(optOutSchema, "notes").validate(nextNotes);
      setErrors((prev) => ({ ...prev, notes: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, notes: err.message }));
    }
  };

  const handleNotesBlur = async () => {
    try {
      await Yup.reach(optOutSchema, "notes").validate(notes);
      setErrors((prev) => ({ ...prev, notes: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, notes: err.message }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const id = String(candidateClientId || "").trim();
    if (!id) {
      toast.error("Candidate ID not found.");
      return;
    }

    const trimmedNotes = String(notes ?? "").trim();

    try {
      await optOutSchema.validate({ notes: trimmedNotes }, { abortEarly: false });
      setErrors({});
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await candidateAiRecruiterOptOut(id, {
        notes: trimmedNotes,
      });
      const responseSucceeded =
        response?.success ||
        response?.isSuccess ||
        response?.status === "success" ||
        response?.status === true;
      if (responseSucceeded) {
        toast.success(response?.message || "AI Recruiter opted out.");
        onSuccess?.();
        onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="md"
      className="modal-full-height"
      centered
      zIndex={5000}
    >
      <Form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">AI Recruiter — Withdraw</h5>
            <Button close type="button" onClick={closeModal} disabled={submitting} />
          </div>
          <hr />

          <FormGroup className="mb-0">
            <Label>
              Notes <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={4}
              value={notes}
              onChange={handleNotesChange}
              onBlur={handleNotesBlur}
              placeholder="Enter notes"
              disabled={submitting}
              invalid={!!errors.notes}
            />
            <FormFeedback>{errors.notes}</FormFeedback>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CallDetailsAiRecruiterOptOutModal;
